import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";

const Header = ({
  user,
  setUser,
  selectedCompany,
  rootLocations = [],
  activeRootLocationId = null,
  setActiveRootLocationId = () => {},
  rightPanelOpen = false,
}) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRootDropdown, setShowRootDropdown] = useState(false);
  const rootDropdownRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootDropdownRef.current && !rootDropdownRef.current.contains(e.target)) {
        setShowRootDropdown(false);
      }
    };
    if (showRootDropdown) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [showRootDropdown]);

  const activeRoot = rootLocations.find((r) => String(r.id) === String(activeRootLocationId)) || null;
  const activeRootLabel = activeRoot ? activeRoot.name : "All";
  
  const handleLogout = async () => {
    setShowDropdown(false);
    await fetch("/auth/logout", {
      credentials: "include",
    });
    setUser(null);
    localStorage.removeItem("user");
    navigate("/");
  };

  const userInitials = (() => {
    if (user?.name && user.name.trim().length > 0) {
      const chunks = user.name.trim().split(/\s+/);
      return chunks.slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("");
    }
    if (user?.email) {
      return user.email[0]?.toUpperCase() || "G";
    }
    return "G";
  })();

  const companyName = selectedCompany?.name || user?.company?.name || user?.selectedCompany?.name || "-";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-slate-900 text-slate-100 flex items-center px-0 z-[150] shadow-md border-b border-slate-800">
        <div className="w-16 h-16 flex items-center justify-center shrink-0 border-r border-slate-800">
          <img
            src="/trace-mop-mark.png"
            alt="Trace Mop"
            className="h-10 w-auto"
            draggable={false}
          />
        </div>

        <div className="ml-3 text-xl font-bold tracking-tight select-none text-slate-100">
          Trace Mop
        </div>

        <div
          className="absolute inset-y-0 pointer-events-none flex items-center justify-center"
          style={{ left: 64, right: rightPanelOpen ? 320 : 0 }}
        >
          <div className="pointer-events-auto flex items-center gap-2 text-slate-100">
            <span className="text-base font-semibold tracking-tight">{companyName}</span>
            <span className="text-slate-600">/</span>
            <div className="relative" ref={rootDropdownRef}>
              <button
                type="button"
                onClick={() => setShowRootDropdown((p) => !p)}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800 text-slate-100 text-base font-semibold transition"
              >
                <span>{activeRootLabel}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {showRootDropdown && (
                <div className="absolute top-full left-0 mt-1 min-w-[220px] bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[220] py-1 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRootLocationId(null);
                      setShowRootDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 hover:bg-slate-700 ${
                      activeRootLocationId === null || activeRootLocationId === undefined
                        ? "text-teal-400 font-semibold"
                        : "text-slate-100"
                    }`}
                  >
                    All
                  </button>
                  {rootLocations.length > 0 && (
                    <div className="border-t border-slate-700 my-1" />
                  )}
                  {rootLocations.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => {
                        setActiveRootLocationId(loc.id);
                        setShowRootDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 hover:bg-slate-700 ${
                        String(activeRootLocationId) === String(loc.id)
                          ? "text-teal-400 font-semibold"
                          : "text-slate-100"
                      }`}
                    >
                      {loc.name}
                    </button>
                  ))}
                  {rootLocations.length === 0 && (
                    <div className="px-3 py-1.5 text-slate-500 italic">
                      Nincs root location
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3 pr-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-xs font-semibold px-4 py-2 rounded border border-teal-500 text-teal-400 hover:bg-teal-500 hover:text-white transition"
          >
            tracemop.com
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDropdown((prev) => !prev)}
            className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-400 transition overflow-hidden border border-slate-700 flex items-center justify-center"
              aria-label="Profil menü megnyitása"
            >
            {user?.photo_url ? (
              <img
                src={user.photo_url}
                alt="User avatar"
                className="w-full h-full object-cover"
                draggable={false}
              />
            ) : (
              <span className="text-xs font-semibold text-slate-100">{userInitials}</span>
            )}
            </button>

            {showDropdown && (
              <div className="fixed top-16 right-4 bg-slate-800 text-slate-100 shadow-xl z-[220] p-4 flex flex-col gap-3 text-xs border border-slate-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-700 border border-slate-600 flex items-center justify-center">
                    {user?.photo_url ? (
                      <img
                        src={user.photo_url}
                        alt="User avatar"
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-slate-100">{userInitials}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-left text-slate-100 cursor-default font-medium">
                      {user?.name || "Google user"}
                    </span>
                    <span className="text-left text-slate-400 cursor-default text-[11px]">
                      {user?.email}
                    </span>
                    <span className="text-left text-teal-300 cursor-default text-[11px]">
                      {companyName}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="text-left hover:text-teal-400 flex items-center gap-1.5"
                >
                  <LogOut size={12} /> Kilépés
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
